import axios from 'axios';

import { httpClient } from '@/core/api/http-client';

import type {
  ChangeStatusResponse,
  EvidenceSignedUrlResponse,
  OrderListFilters,
  OrderListResponse,
  OrderMetrics,
} from './orders.types';

export const ordersService = {
  /**
   * Returns a paginated list of all orders.
   * Endpoint: GET /admin/orders
   *
   * @param filters - Optional filters: search, search_field, sort_by, sort_order, status, page, limit, from, to.
   * @returns Paginated items, total count and current page.
   */
  list: async (filters?: OrderListFilters): Promise<OrderListResponse> => {
    const response = await httpClient.get<OrderListResponse>('/admin/orders', {
      params: filters,
    });
    return response.data;
  },

  /**
   * Returns order metrics by status for the current country.
   * Endpoint: GET /admin/orders/metrics
   */
  metrics: async (): Promise<OrderMetrics> => {
    const response = await httpClient.get<OrderMetrics>('/admin/orders/metrics');
    return response.data;
  },

  /**
   * Changes the status of multiple orders.
   * Endpoint: POST /admin/orders/change_status
   */
  changeStatus: async (payload: {
    order_refs: string[];
    action: 'delete' | string;
    motivo: string;
    evidence_gcs_path?: string;
  }): Promise<ChangeStatusResponse> => {
    const response = await httpClient.post<ChangeStatusResponse>(
      '/admin/orders/change_status',
      payload,
    );
    return response.data;
  },

  /**
   * Requests a GCS signed URL for order evidence upload.
   * Endpoint: GET /admin/orders/evidence/upload_url
   *
   * @param external_reference - The order's tracking code.
   * @param ext - The file extension (e.g. "png", "jpg").
   */
  getEvidenceSignedUrl: async (
    external_reference: string,
    ext: string,
  ): Promise<EvidenceSignedUrlResponse> => {
    const response = await httpClient.get<EvidenceSignedUrlResponse>(
      '/admin/orders/evidence/upload_url',
      {
        params: { external_reference, ext },
      },
    );
    return response.data;
  },

  /**
   * Requests a signed URL to read an evidence file from GCS.
   * Endpoint: GET /admin/orders/evidence/read_url
   */
  getEvidenceReadUrl: async (gcs_path: string): Promise<{ read_url: string }> => {
    const response = await httpClient.get<{ read_url: string }>(
      '/admin/orders/evidence/read_url',
      {
        params: { gcs_path },
      },
    );
    return response.data;
  },

  /**
   * Uploads a file directly to GCS using a pre-signed PUT URL.
   */
  uploadToGcs: async (upload_url: string, file: File): Promise<void> => {
    await axios.put(upload_url, file, {
      headers: { 'Content-Type': file.type },
    });
  },
};
